// Additive optional comparisons only. Existing report metrics are not replaced.
export type StoreOverviewLySource = {
  net_sales_uzs: number;
  net_units: number;
  receipts: number;
  average_unit_price_uzs: number;
  previous_year_sales_uzs: number;
  previous_year_units: number;
  previous_year_receipts?: number;
};
export type StoreOverviewLyValues = Record<"lyReceipts" | "receiptsChange" | "lyUnits" | "unitsChange" | "lyUnitPrice" | "unitPriceChange", number | null>;
const change = (current: number, previous: number | null) => previous == null || previous === 0 ? null : (current - previous) / Math.abs(previous) * 100;
// These formatters have fixed locale/options; reuse them across rows and renders.
const integer = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 });
const percent = new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

export function storeOverviewOptionalValues(row: StoreOverviewLySource): StoreOverviewLyValues {
  const lyReceipts = row.previous_year_receipts ?? null;
  const lyUnitPrice = row.previous_year_units !== 0 ? row.previous_year_sales_uzs / row.previous_year_units : null;
  return {
    lyReceipts,
    receiptsChange: change(row.receipts, lyReceipts),
    lyUnits: row.previous_year_units,
    unitsChange: change(row.net_units, row.previous_year_units),
    lyUnitPrice,
    unitPriceChange: change(row.average_unit_price_uzs, lyUnitPrice),
  };
}

export function storeOverviewOptionalTotals(rows: StoreOverviewLySource[], current: { sales: number; units: number; receipts: number }): StoreOverviewLyValues {
  return storeOverviewOptionalValues({
    net_sales_uzs: current.sales,
    net_units: current.units,
    receipts: current.receipts,
    average_unit_price_uzs: current.units !== 0 ? current.sales / current.units : 0,
    previous_year_sales_uzs: rows.reduce((sum, row) => sum + row.previous_year_sales_uzs, 0),
    previous_year_units: rows.reduce((sum, row) => sum + row.previous_year_units, 0),
    previous_year_receipts: rows.every(row => row.previous_year_receipts != null)
      ? rows.reduce((sum, row) => sum + row.previous_year_receipts!, 0) : undefined,
  });
}

export function storeOverviewOptionalDisplay(values: StoreOverviewLyValues): Record<string, string | number | null> {
  return Object.fromEntries(Object.entries(values).flatMap(([key, value]) => [
    [key, value == null ? "—" : key.endsWith("Change") ? `${value >= 0 ? "+" : ""}${percent.format(value)}%` : integer.format(value)],
    ["__sort_" + key, value],
  ]));
}
